import React, { useMemo } from 'react';
import { QuestionRendererProps } from './QuestionRenderer';
import { DosingScheduleValue } from '../../../types/survey.types';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const DEFAULT_DOSE_UNITS = ['mL', 'IU', 'mcg'];
const DEFAULT_SELECTED = [true, true, true, true, true, false, false]; // Mon-Fri on

const FREQUENCY_LABELS: Record<number, string[]> = {
  1: ['Dose'],
  2: ['Morning', 'Evening'],
  3: ['Morning', 'Midday', 'Evening'],
  4: ['Morning', 'Midday', 'Afternoon', 'Evening'],
};

const DosingScheduleRenderer: React.FC<Omit<QuestionRendererProps, 'questionNumber'>> = ({
  question,
  value,
  onChange,
  error,
}) => {
  const schedule: DosingScheduleValue = value && typeof value === 'object'
    ? value as DosingScheduleValue
    : {
        daysOn: 5,
        daysOff: 2,
        selectedDays: [...DEFAULT_SELECTED],
        cycleDuration: 4,
        cycleDurationUnit: (question.settings?.defaultCycleUnit as 'weeks' | 'months') || 'weeks',
        frequencyPerDay: 1,
        doseAmount: undefined,
        doseUnit: question.settings?.availableDoseUnits?.[0] || 'mL',
      };

  // Ensure selectedDays exists (backward compat with data saved before this field)
  const selectedDays = schedule.selectedDays && schedule.selectedDays.length === 7
    ? schedule.selectedDays
    : DEFAULT_SELECTED;

  // Backward compat: default frequencyPerDay to 1
  const freq = schedule.frequencyPerDay || 1;

  const showDose = question.settings?.includeDoseAmount !== false;
  const maxDuration = question.settings?.maxCycleDuration || 52;
  const doseUnits = question.settings?.availableDoseUnits || DEFAULT_DOSE_UNITS;

  const update = (partial: Partial<DosingScheduleValue>) => {
    const updated = { ...schedule, ...partial };
    // Recalculate daysOn/daysOff from selectedDays
    const days = updated.selectedDays || selectedDays;
    updated.daysOn = days.filter(Boolean).length;
    updated.daysOff = 7 - updated.daysOn;
    onChange(updated);
  };

  const toggleDay = (index: number) => {
    const newDays = [...selectedDays];
    newDays[index] = !newDays[index];
    // Require at least 1 day on
    if (newDays.filter(Boolean).length === 0) return;
    update({ selectedDays: newDays });
  };

  const daysOn = selectedDays.filter(Boolean).length;
  const daysOff = 7 - daysOn;

  // Get labels for the current frequency
  const doseLabels = FREQUENCY_LABELS[freq] || Array.from({ length: freq }, (_, i) => `Dose ${i + 1}`);

  // Get the doseAmounts array, initializing if needed
  const doseAmounts = schedule.doseAmounts && schedule.doseAmounts.length === freq
    ? schedule.doseAmounts
    : doseLabels.map((label, i) => ({
        label,
        amount: i === 0 && schedule.doseAmount ? schedule.doseAmount : 0,
      }));

  const handleFrequencyChange = (newFreq: number) => {
    const labels = FREQUENCY_LABELS[newFreq] || Array.from({ length: newFreq }, (_, i) => `Dose ${i + 1}`);
    if (newFreq === 1) {
      // Collapse back to single dose
      update({
        frequencyPerDay: 1,
        doseAmounts: undefined,
        doseAmount: doseAmounts[0]?.amount || schedule.doseAmount,
      });
    } else {
      // Expand to multiple doses
      const newAmounts = labels.map((label, i) => ({
        label,
        amount: doseAmounts[i]?.amount || (i === 0 ? (schedule.doseAmount || 0) : 0),
      }));
      update({
        frequencyPerDay: newFreq,
        doseAmounts: newAmounts,
      });
    }
  };

  const handleDoseAmountChange = (index: number, amount: number | undefined) => {
    if (freq === 1) {
      update({ doseAmount: amount });
    } else {
      const newAmounts = [...doseAmounts];
      newAmounts[index] = { ...newAmounts[index], amount: amount || 0 };
      update({ doseAmounts: newAmounts });
    }
  };

  const { totalAdministrations, totalDose } = useMemo(() => {
    const totalWeeks = schedule.cycleDurationUnit === 'months'
      ? schedule.cycleDuration * 4.33
      : schedule.cycleDuration;
    const totalDays = Math.round(daysOn * totalWeeks);
    const totalAdmin = totalDays * freq;

    let dose: number | undefined;
    if (freq === 1 && schedule.doseAmount) {
      dose = schedule.doseAmount * totalDays;
    } else if (freq > 1 && doseAmounts.some(d => d.amount > 0)) {
      const dailyDose = doseAmounts.reduce((sum, d) => sum + (d.amount || 0), 0);
      dose = dailyDose * totalDays;
    }
    return { totalAdministrations: totalAdmin, totalDose: dose };
  }, [daysOn, schedule.cycleDuration, schedule.cycleDurationUnit, schedule.doseAmount, freq, doseAmounts]);

  const borderClass = error
    ? 'border-red-300'
    : 'border-gray-200';

  const inputClass = `px-4 py-2.5 text-base border-2 ${borderClass} rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none`;

  // Build the day names for the summary (e.g., "Mon, Wed, Fri")
  const activeDayNames = DAY_LABELS.filter((_, i) => selectedDays[i]);

  return (
    <div className="space-y-5">
      {/* Interactive Weekly Calendar */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Which days do you administer? <span className="text-gray-400 font-normal">(tap to toggle)</span>
        </label>
        <div className="flex gap-2">
          {DAY_LABELS.map((day, i) => {
            const isOn = selectedDays[i];
            return (
              <button
                key={i}
                type="button"
                onClick={() => toggleDay(i)}
                className={`flex flex-col items-center gap-1 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-xl p-1 transition-all ${
                  isOn ? '' : 'opacity-80'
                }`}
                aria-label={`${day}: ${isOn ? 'administration day, click to remove' : 'rest day, click to add'}`}
                aria-pressed={isOn}
              >
                <div
                  className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-semibold transition-all cursor-pointer ${
                    isOn
                      ? 'bg-blue-500 text-white shadow-md hover:bg-blue-600'
                      : 'bg-gray-200 text-gray-400 hover:bg-gray-300'
                  }`}
                >
                  {isOn ? '✓' : '–'}
                </div>
                <span className={`text-xs font-medium ${isOn ? 'text-blue-600' : 'text-gray-400'}`}>
                  {day}
                </span>
              </button>
            );
          })}
        </div>
        <p className="text-sm text-gray-500 mt-2">
          <span className="font-medium text-gray-700">{daysOn} day{daysOn !== 1 ? 's' : ''} on</span>, {daysOff} day{daysOff !== 1 ? 's' : ''} off per week
        </p>
      </div>

      {/* Frequency per day */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          How many times per day do you dose?
        </label>
        <div className="flex gap-2">
          {[1, 2, 3].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => handleFrequencyChange(n)}
              className={`px-4 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${
                freq === n
                  ? 'bg-blue-500 text-white border-blue-500 shadow-md'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
              }`}
            >
              {n}x daily
            </button>
          ))}
        </div>
      </div>

      {/* Duration */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          How long do you follow this protocol?
        </label>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min={1}
            max={maxDuration}
            value={schedule.cycleDuration || ''}
            onChange={(e) => {
              const val = parseInt(e.target.value);
              if (!isNaN(val) && val >= 1 && val <= maxDuration) {
                update({ cycleDuration: val });
              } else if (e.target.value === '') {
                update({ cycleDuration: 0 as any });
              }
            }}
            className={`w-24 ${inputClass}`}
          />
          <select
            value={schedule.cycleDurationUnit}
            onChange={(e) => update({ cycleDurationUnit: e.target.value as 'weeks' | 'months' })}
            className={`${inputClass} cursor-pointer`}
          >
            <option value="weeks">weeks</option>
            <option value="months">months</option>
          </select>
        </div>
      </div>

      {/* Dose Amount(s) */}
      {showDose && (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            {freq > 1 ? 'Dose per administration' : 'Dose per administration'}{' '}
            <span className="text-gray-400 font-normal">(optional)</span>
          </label>

          {freq === 1 ? (
            /* Single dose input */
            <div className="flex items-center gap-3">
              <input
                type="number"
                min={0}
                step="any"
                value={schedule.doseAmount ?? ''}
                onChange={(e) => {
                  const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
                  update({ doseAmount: val });
                }}
                placeholder="e.g., 250"
                className={`w-32 ${inputClass}`}
              />
              <select
                value={schedule.doseUnit || doseUnits[0]}
                onChange={(e) => update({ doseUnit: e.target.value })}
                className={`${inputClass} cursor-pointer`}
              >
                {doseUnits.map((unit) => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
              </select>
            </div>
          ) : (
            /* Multiple dose inputs with time-of-day labels */
            <div className="space-y-3">
              <div className="flex items-center gap-3 mb-1">
                <span className="text-sm text-gray-500 w-32">Unit:</span>
                <select
                  value={schedule.doseUnit || doseUnits[0]}
                  onChange={(e) => update({ doseUnit: e.target.value })}
                  className={`${inputClass} cursor-pointer`}
                >
                  {doseUnits.map((unit) => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </select>
              </div>
              {doseLabels.map((label, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-600 w-32">{label}:</span>
                  <input
                    type="number"
                    min={0}
                    step="any"
                    value={doseAmounts[i]?.amount || ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
                      handleDoseAmountChange(i, val);
                    }}
                    placeholder={`${label} dose`}
                    className={`w-32 ${inputClass}`}
                  />
                  <span className="text-sm text-gray-500">{schedule.doseUnit || doseUnits[0]}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Summary Card */}
      {daysOn > 0 && schedule.cycleDuration > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-1">
          <p className="text-base font-semibold text-blue-900">
            {activeDayNames.join(', ')} ({daysOn} day{daysOn !== 1 ? 's' : ''} on, {daysOff} off) &middot; {freq}x daily for {schedule.cycleDuration} {schedule.cycleDurationUnit}
          </p>
          <p className="text-sm text-blue-700">
            {totalAdministrations} total administration{totalAdministrations !== 1 ? 's' : ''}
          </p>
          {freq > 1 && doseAmounts.some(d => d.amount > 0) && (
            <p className="text-sm text-blue-700">
              {doseAmounts.map(d => `${d.label}: ${d.amount} ${schedule.doseUnit || doseUnits[0]}`).join(', ')}
            </p>
          )}
          {freq === 1 && schedule.doseAmount && (
            <p className="text-sm text-blue-700">
              at {schedule.doseAmount} {schedule.doseUnit} per administration
            </p>
          )}
          {totalDose !== undefined && totalDose > 0 && (
            <p className="text-sm text-blue-700 font-medium">
              {totalDose.toLocaleString()} {schedule.doseUnit || doseUnits[0]} total
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default DosingScheduleRenderer;
